import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode, Download, Printer, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GOLD, MUTED, NAVY } from "@/components/om/OmBits";

const saveBlob = (res, fallback) => {
  const name = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || fallback;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const AssetQrDialog = ({ asset, open, onOpenChange }) => {
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState("");
  const base = window.location.origin;

  useEffect(() => {
    if (!open || !asset) return;
    let url = "";
    api.get(`/om/assets/${asset.id}/qr`, { params: { fmt: "png", base }, responseType: "blob" })
      .then((res) => { url = URL.createObjectURL(res.data); setPreview(url); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
    return () => { if (url) URL.revokeObjectURL(url); setPreview(""); };
  }, [open, asset, base]);

  const grab = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await api.get(`/om/assets/${asset.id}/qr`, { params: { fmt, base }, responseType: "blob" });
      saveBlob(res, fmt === "png" ? `qr-${asset.code}.png` : `label-${asset.code}.pdf`);
      toast.success(fmt === "png" ? "QR image downloaded" : "A6 print label downloaded");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  if (!asset) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" style={{ background: "#112240", borderColor: `${GOLD}33` }} data-testid="om-asset-qr-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <QrCode className="w-4 h-4" style={{ color: GOLD }} /> {asset.code} · QR tag
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center">
          <div className="bg-white p-3" style={{ borderRadius: 8, minHeight: 180, minWidth: 180 }}>
            {preview ? <img src={preview} alt={`QR for ${asset.code}`} width={170} height={170} data-testid="om-asset-qr-image" />
              : <div className="flex items-center justify-center h-[170px] w-[170px]"><Loader2 className="w-5 h-5 animate-spin" style={{ color: NAVY }} /></div>}
          </div>
          <p className="text-[11.5px] mt-3 text-center" style={{ color: MUTED }}>
            Scanning opens the mobile log page for <span className="text-white">{asset.name}</span> — a technician can file a reading or raise a fault in seconds.
          </p>
          <div className="flex gap-2 mt-4">
            <Button size="sm" disabled={!!busy} onClick={() => grab("png")} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-asset-qr-png-btn">
              {busy === "png" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} PNG
            </Button>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => grab("label")} style={{ borderColor: `${GOLD}66`, color: GOLD, background: "transparent" }} data-testid="om-asset-qr-label-btn">
              {busy === "label" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1.5" />} A6 print label
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
